import { Picture } from "@/components/site/Picture";
import { STATS_TEXT } from "@/lib/stats";
import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { CTAButton } from "@/components/site/Button";
import { StrategyCallButton } from "@/components/site/StrategyCallDialog";
import { BRAND, absoluteUrl } from "@/lib/brand";

export const Route = createFileRoute("/kristan")({
  head: () => ({
    meta: [
      { title: "Kristan Cole - Real Estate Leader, Mentor, KCN Founder" },
      {
        name: "description",
        content:
          "Kristan Cole has spent 40+ years leading real estate teams. Founder of the Kristan Cole Network, she coaches agents from chaos to clarity and producer to CEO.",
      },
      { property: "og:title", content: "About Kristan Cole" },
      {
        property: "og:description",
        content:
          "40+ years in real estate, $1B+ in 2025 volume, founder of the COLE Method™ and Kristan's Home of Hope.",
      },
      { property: "og:url", content: "https://joinkcn.com/kristan" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/kristan" }],
    scripts: [
      breadcrumbSchema([{ name: "Kristan Cole", path: "/kristan" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Kristan Cole",
          jobTitle: "Founder & CEO, Kristan Cole Network",
          description:
            `Real estate icon, dynamic speaker, and expert in business expansion. 40+ years of leadership; $1B+ in 2025 volume across 3,000+ transactions; leader of a KW network across ${STATS_TEXT.markets} markets.`,
          worksFor: { "@type": "Organization", name: "Kristan Cole Network" },
          affiliation: { "@type": "Organization", name: "Keller Williams Realty" },
          knowsAbout: [
            "Real estate team leadership",
            "Wealth building for agents",
            "Real estate expansion",
            "Agent coaching",
            "The COLE Method",
          ],
          award: [
            "Realtor of the Year",
            "2018 Inman Innovator Finalist",
          ],
          sameAs: [
            "https://www.linkedin.com/company/kristancolenetwork/",
            "https://www.instagram.com/kristancolenetwork/",
          ],
          image: absoluteUrl(BRAND.profilePics[0]),
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">About Kristan · Real Estate Icon</p>
            <h1 className="headline-xl">
              A career built on
              <br />
              <span className="serif-italic text-primary">clarity, not noise.</span>
            </h1>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-foreground/85 text-base">
              {[
                "40+ years in real estate",
                "Leader of a KW network",
                "Mentee of John C. Maxwell & Gary Keller",
                "Realtor of the Year",
                "2018 Inman Innovator finalist",
                "Founder, Kristan's Home of Hope",
              ].map((r) => (
                <li key={r} className="flex items-start gap-3">
                  <span className="text-primary mt-2 text-xs">◆</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-foreground/90 text-lg leading-relaxed">
              Kristan Cole is a real estate icon, dynamic speaker, and expert
              in business expansion. She leads a national network of KW
              brokerages - and mentors the agents inside it like a CEO, not a
              cheerleader.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="container-edge py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Picture
              src={BRAND.profilePics[1]}
              alt="Kristan Cole - portrait"
              className="w-full aspect-[4/5] object-cover"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-foreground/90 text-lg leading-relaxed">
            <p>
              For more than four decades, Kristan has built and led real estate
              organizations through every market cycle. In 2025, her team closed
              more than <strong className="text-foreground">$1 billion in volume</strong>{" "}
              across more than <strong className="text-foreground">3,000 transactions</strong>.
            </p>
            <p>
              But ask anyone who's worked with her, and the numbers aren't what
              they lead with. They lead with the same word: <em>clarity</em>. The
              calm, faith-anchored, CEO-level clarity that turns chaos into
              compounding growth.
            </p>
            <p>
              That clarity is now codified in the COLE Method™ - the framework
              every agent on her team uses to scale without burning out.
            </p>
          </div>
        </div>
      </section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionEyebrow>A letter from Kristan</SectionEyebrow>
            <h2 className="headline-lg">
              Why I do
              <br />
              <span className="serif-italic text-primary">what I do.</span>
            </h2>
            <p className="mt-8 font-display italic text-2xl text-foreground/90 leading-snug">
              "Grit, Grace, Growth, and Give."
            </p>
            <p className="mt-4 text-sm uppercase tracking-[0.2em] text-foreground/55">
             - Kristan Cole
            </p>
          </div>

          <div className="lg:col-span-7 lg:col-start-6 space-y-12 text-foreground/90 text-lg leading-[1.75]">
            <p>
              I have been an agent for the last 40 years and I understand value.
              I also own multiple market centers here in Arizona. Last year the
              sales numbers reported that{" "}
              <strong className="text-foreground">
                92% of agents in the nation did under 4 transactions in one year.
              </strong>{" "}
              I thought to myself, "WOW! How bad is that…"
            </p>

            <div>
              <h3 id="how-i-got-started" className="font-display text-3xl text-primary mb-5">
                How I got started
              </h3>
              <p>
                Early in my career I was a single mom raising 5 children on my
                own. I needed to make a living, and real estate gave me a way to
                build one - on my terms. I wasn't born into this business. I
                wasn't handed a market. I built it, deal by deal, season by
                season, with grit and grace and a whole lot of prayer.
              </p>
            </div>

            <div>
              <h3 id="industry" className="font-display text-3xl text-primary mb-5">
                What I want for the industry
              </h3>
              <p>
                The industry doesn't have a talent problem. It has a clarity
                problem. Agents are working harder than ever and getting less
                back. I want to change that - for the agents on my team, and for
                anyone willing to actually do the work the right way.
              </p>
            </div>

            <div>
              <h3 id="built-with-the-best" className="font-display text-3xl text-primary mb-5">
                Built with the best
              </h3>
              <p>
                I've surrounded myself with the best operators, coaches, leaders
                and systems I could find. Then I documented all of it so the
                agents who join us don't have to reinvent anything. You step
                into a framework that already works.
              </p>
            </div>

            <div>
              <h3 id="changing-lives" className="font-display text-3xl text-primary mb-5">
                Changing lives
              </h3>
              <p>
                This isn't about a number on a leaderboard. It's about an agent
                taking her family on the vacation she's been promising for
                years. It's about a dad finally being home for dinner. It's
                about wealth that compounds - and a life that doesn't get traded
                for it.
              </p>
            </div>

            <div className="border-y border-border py-10">
              <h3 id="does-this-describe-you" className="font-display text-3xl text-primary mb-5">
                Does this describe you?
              </h3>
              <p>
                You have grit. You're coachable. You want to grow - as an
                operator and as a person. And when you win, you want to give
                back. If you read that and your chest tightened a little because
                yes, that's me - keep going.
              </p>
            </div>

            <div>
              <h3 id="giving-back" className="font-display text-3xl text-primary mb-5">
                Giving back
              </h3>
              <p>
                Every part of this business funds something bigger. Kristan's
                Home of Hope exists because success that doesn't pour back out
                isn't success. It's just income.
              </p>
            </div>

            <div>
              <h3 id="join-the-team" className="font-display text-3xl text-primary mb-5">
                Join the #1 team
              </h3>
              <p>
                We will get there with you, or we will get there without you - 
                but we are getting there. If you want to join us, we are
                interviewing now. You have to want this. You have to have the
                <strong className="text-foreground"> GRIT</strong> to change your
                life and your family's life. We will be number one and win the
                Super Bowl of teams.
              </p>
              <p className="mt-6">
                Come join us. Join our family. I promise - if you follow
                everything we show you and commit with the right attitude,
                you'll have the success I've had, and the life that goes with
                it.
              </p>
              <p className="mt-8 font-display italic text-2xl text-foreground">
               - Kristan
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="cream">
        <div className="grid md:grid-cols-3 gap-12">
          {[
            {
              t: "Operator",
              b: "40+ years leading expansion teams across multiple states. A business built and stress-tested through every cycle real estate can throw.",
            },
            {
              t: "Mentor",
              b: "Coaches her team like a CEO - strategic, calm, and grounded. Encouraging without enabling. Specific without being prescriptive.",
            },
            {
              t: "Philanthropist",
              b: "Founder of Kristan's Home of Hope - a nonprofit dedicated to caring for vulnerable women and children. Faith and family, in practice.",
            },
          ].map((c) => (
            <div key={c.t}>
              <h3 className="font-display text-3xl mb-4 text-primary">{c.t}</h3>
              <p className="text-cream-foreground/85 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>What she believes</SectionEyebrow>
            <h2 className="headline-lg">
              Faith, family, and legacy
              <br />
              <span className="serif-italic text-primary">over hustle.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-border border-y border-border">
              {[
                "Clarity brings peace.",
                "Structure creates freedom.",
                "Slow down to go fast.",
                "Lead with alignment, not adrenaline.",
                "Your business should fund your life, not consume it.",
                "Build what lasts.",
              ].map((q) => (
                <li key={q} className="py-5 font-display text-2xl">
                  "{q}"
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionEyebrow>Speaking · Writing · Coaching</SectionEyebrow>
            <h2 className="headline-md">
              When Kristan isn't leading her team, she's teaching others how to
              lead theirs.
            </h2>
            <p className="mt-6 text-foreground/90 text-lg leading-relaxed">
              Kristan speaks at industry events nationwide, has authored work on
              downsizing for homeowners, and mentors leaders inside and outside
              real estate. Whether on stage or one-on-one, her message is the
              same: there's a way to build that doesn't cost you everything else
              that matters.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <StrategyCallButton>
                Book a Growth Strategy Call
              </StrategyCallButton>
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Picture
              src={BRAND.profilePics[2]}
              alt="Kristan Cole speaking"
              className="w-full aspect-square object-cover object-top"
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      <CTABanner cta="roundtable" />
    </SiteLayout>
  );
}
